import RequestBuilder from 'Resources/RequestBuilder';
import FieldTranslation from 'App/babel/resources/FieldTranslations';
import { getLiveTags, LiveTagMap, SocialEventLike } from 'App/services/SocialEventTags';
import logError from 'App/babel/helpers/logError';
import T from 'Components/Helpers/translate';
import AppService from 'App/legacy/AppService';
import { Feature, isAvailable, hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import { getTagCatalogue } from 'App/services/EntityTags';
import type CustomField from 'App/resources/Model/CustomField';

/*
	The mail tag catalogue, extracted from the Angular TagService/TagsService (WEB-11618). Serves the
	{{Tag}} pickers in the mail editors — InsertTag, the Unlayer merge tags and the mail template
	editors.

	Built lazily for the same reason as EntityTags.ts: every entry is a translated string and the
	catalogue depends on metadata (standard fields, custom fields, activated features), so it cannot
	be built at module load. The Angular service built it inside AppService.loadedPromise;
	buildMailTags() is memoised and runs on first read instead.

	Behaviour is preserved verbatim, quirks included — see the comments on getMailTags and
	getTagsByEntity2. This is a decouple, not a cleanup.
*/

export type MailTag = {
	name: string;
	value: string;
	html?: boolean;
	raw?: boolean;
	group?: string;
	icon?: string;
	subtitle?: string;
	tag?: string;
	description?: string;
	type?: string;
};

export type MailTagGroup = {
	title: string;
	group?: string;
	class?: string;
	tags: MailTag[];
	sort?: number;
};

export type MailTagListConfig = {
	tagEntity?: string;
	hideIdTags?: boolean;
	socialEvent?: Record<string, unknown>;
	excludeFormSubmit?: boolean;
};

type MailTagDefinition = {
	tag: string;
	lang: string;
	raw: boolean;
	group: string;
	html?: boolean;
	isPopular?: boolean;
	idTag?: boolean;
};

type StandardField = { active: boolean };
type Language = { language: string; name: string; country?: string; isSelected?: boolean };
type CustomFieldKey = Parameters<typeof AppService.getCustomFields>[0];

const t = T;

const GROUP_SORT: { [group: string]: number } = {
	Popular: 1,
	Client: 2,
	Contact: 3,
	Project: 4,
	General: 5,
	Special: 6,
	FormValues: 7,
	SocialEvent: 8
};

const getGroupSort = (group: string) => GROUP_SORT[group];

const getTag = (tag: string, title: string, raw?: boolean, html?: boolean): MailTag => ({
	name: title,
	value: raw ? tag : '{{' + tag + '}}',
	html: html || false
});

const sortByName = <T extends { name: string }>(items: T[]): T[] =>
	[...items].sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0));

let cached: { tags: MailTagDefinition[]; popularTags: MailTagDefinition[] } | null = null;

const buildMailTags = () => {
	if (cached) {
		return cached;
	}

	const tags: MailTagDefinition[] = [
		// Contact
		{ tag: 'Contact.FirstName', lang: 'mailTags.fname', raw: false, group: 'Contact', isPopular: true },
		{ tag: 'Contact.LastName', lang: 'mailTags.lname', raw: false, group: 'Contact', isPopular: true },
		{ tag: 'Contact.Name', lang: 'mailTags.name', raw: false, group: 'Contact', isPopular: true },
		{ tag: 'Contact.Email', lang: 'mailTags.email', raw: false, group: 'Contact' },
		{ tag: 'Contact.Id', lang: 'tag.contact.id', raw: false, group: 'Contact', idTag: true },
		{ tag: 'Contact.Phone', lang: 'tag.contact.phone', raw: false, group: 'Contact' },
		{ tag: 'Contact.CellPhone', lang: 'tag.contact.cellphone', raw: false, group: 'Contact' },
		{ tag: 'Contact.Title', lang: 'tag.contact.title', raw: false, group: 'Contact' },
		{ tag: 'Contact.Notes', lang: 'tag.contact.notes', raw: false, group: 'Contact' },
		{ tag: 'Contact.ClientId', lang: 'tag.contact.clientid', raw: false, group: 'Contact', idTag: true },
		{ tag: 'Contact.RegById', lang: 'tag.contact.regbyid', raw: false, group: 'Contact', idTag: true },
		{ tag: 'Contact.RegByName', lang: 'tag.contact.regbyname', raw: false, group: 'Contact' },
		{ tag: 'Contact.RegByEmail', lang: 'tag.contact.regbyemail', raw: false, group: 'Contact' },
		{ tag: 'Contact.Active', lang: 'tag.contact.active', raw: false, group: 'Contact' },

		// Client
		{
			tag: 'Client.UserName',
			lang: 'mailTags.account_manager',
			raw: false,
			group: 'Client',
			isPopular: true
		},
		{
			tag: 'Client.UserEmail',
			lang: 'mailTags.account_manager_email',
			raw: false,
			group: 'Client',
			isPopular: true
		},
		{ tag: 'Client.UserPhone', lang: 'mailTags.account_manager_phone', raw: false, group: 'Client' },
		{
			tag: 'Client.UserCellPhone',
			lang: 'mailTags.account_manager_cellphone',
			raw: false,
			group: 'Client'
		},
		{ tag: 'Client.Name', lang: 'mailTags.client_name', raw: false, group: 'Client', isPopular: true },
		{ tag: 'Client.Phone', lang: 'mailTags.client_phone', raw: false, group: 'Client' },
		{ tag: 'Client.Id', lang: 'tag.account.id', raw: false, group: 'Client', idTag: true },
		{ tag: 'Client.Address', lang: 'tag.account.address', raw: false, group: 'Client' },
		{ tag: 'Client.ZipCode', lang: 'tag.account.zipcode', raw: false, group: 'Client' },
		{ tag: 'Client.State', lang: 'tag.account.state', raw: false, group: 'Client' },
		{ tag: 'Client.City', lang: 'tag.account.city', raw: false, group: 'Client' },
		{ tag: 'Client.Fax', lang: 'tag.account.fax', raw: false, group: 'Client' },
		{ tag: 'Client.Www', lang: 'tag.account.www', raw: false, group: 'Client' },
		{ tag: 'Client.Notes', lang: 'tag.account.notes', raw: false, group: 'Client' },

		{ tag: 'Client.UserId', lang: 'tag.account.userid', raw: false, group: 'Client', idTag: true },
		{ tag: 'Client.RegById', lang: 'tag.account.regbyid', raw: false, group: 'Client', idTag: true },
		{ tag: 'Client.RegByName', lang: 'tag.account.regbyname', raw: false, group: 'Client' },
		{ tag: 'Client.RegByEmail', lang: 'tag.account.regbyemail', raw: false, group: 'Client' },
		{ tag: 'Client.Active', lang: 'tag.account.active', raw: false, group: 'Client', idTag: true },
		{ tag: 'Client.RelatedProject', lang: 'tag.account.relatedproject', raw: false, group: 'Client' },
		{ tag: 'Client.RelatedCategory', lang: 'tag.account.relatedcategory', raw: false, group: 'Client' },

		// General
		{ tag: 'General.Today', lang: 'date.today', raw: false, group: 'General' },
		{ tag: 'General.ClearField', lang: 'tag.general.clearfield', raw: false, group: 'General' },
		{ tag: 'General.CurrentYear', lang: 'date.currentYear', raw: false, group: 'General' },
		{ tag: 'General.CurrentUserName', lang: 'mailTags.selfName', raw: false, group: 'General' },
		{ tag: 'General.CurrentUserEmail', lang: 'mailTags.selfEmail', raw: false, group: 'General' },
		{ tag: 'General.CurrentUserPhone', lang: 'mailTags.selfPhone', raw: false, group: 'General' },
		{ tag: 'General.CurrentUserCellPhone', lang: 'mailTags.selfCellPhone', raw: false, group: 'General' },
		{ tag: 'General.CurrentUserTitle', lang: 'mailTags.selfTitle', raw: false, group: 'General' },
		{ tag: 'General.CompanyName', lang: 'mailTags.company_name', raw: false, group: 'General' },
		{ tag: 'General.CompanyPhone', lang: 'mailTags.company_phone', raw: false, group: 'General' },
		{ tag: 'General.CompanyAddress', lang: 'mailTags.company_address', raw: false, group: 'General' },
		{ tag: 'General.CompanyZipCode', lang: 'mailTags.company_zip', raw: false, group: 'General' },
		{ tag: 'General.CompanyCity', lang: 'mailTags.company_city', raw: false, group: 'General' },
		{ tag: 'General.CompanyCountry', lang: 'mailTags.company_country', raw: false, group: 'General' },
		{ tag: 'General.CompanyDisclaimer', lang: 'mailTags.company_disclaimer', raw: false, group: 'General' },

		// Project
		{ tag: 'Project.Name', lang: 'mailTags.project_name', raw: false, group: 'Project' },
		{ tag: 'Project.StartDate', lang: 'mailTags.project_startDate', raw: false, group: 'Project' },
		{ tag: 'Project.EndDate', lang: 'mailTags.project_endDate', raw: false, group: 'Project' },
		{ tag: 'Project.Notes', lang: 'mailTags.project_notes', raw: false, group: 'Project' },

		// Special email tags
		{
			tag: '<a href="{{UNSUB}}" ng-non-bindable>' + t('mailTags.unsubscribeValue') + '</a>',
			lang: 'mailTags.unsubscribe',
			raw: true,
			html: true,
			group: 'Special',
			isPopular: true
		},
		{ tag: 'UNSUB', lang: 'mailTags.unsubscribeUrl', raw: false, group: 'Special' },
		{
			tag: '<a href="{{SUBSCRIPTIONS}}" ng-non-bindable>' + t('mailTags.subscriptions') + '</a>',
			lang: 'mailTags.subscriptions',
			raw: true,
			html: true,
			group: 'Special',
			isPopular: true
		},
		{
			tag: '<a href="{{Mail.BrowserLink}}" ng-non-bindable>' + t('mailTags.openEmailInBrowser') + '</a>',
			lang: 'mailTags.openEmailInBrowserLink',
			raw: true,
			html: true,
			group: 'Special',
			isPopular: true
		}
	];

	if (AppService.getMetadata().activatedFeatures.easyBooking.active) {
		tags.push({
			tag: 'User.AppointmentBookingLink',
			lang: 'mailTags.userEasyBooking',
			raw: false,
			group: 'Appointment'
		});
		tags.push({
			tag: 'Client.UserAppointmentBookingLink',
			lang: 'mailTags.accountManagerEasyBooking',
			raw: false,
			group: 'Appointment'
		});
	}

	if (isAvailable(Feature.PRICE_LISTS)) {
		tags.push({
			tag: 'Client.PriceList',
			lang: 'tag.account.priceList',
			raw: false,
			group: 'Client'
		});
	}

	if (hasSoftDeployAccess(Feature.NEW_FIELDS)) {
		const clientFields: { [field: string]: StandardField } = AppService.getMetadata().standardFields.Client;
		const contactFields: { [field: string]: StandardField } = AppService.getMetadata().standardFields.Contact;

		if (contactFields.Salutation && contactFields.Salutation.active) {
			tags.push({
				tag: 'Contact.Salutation',
				lang: 'tag.contact.salutationCode',
				raw: false,
				group: 'Contact'
			});
			tags.push({
				tag: 'Contact.SalutationValue',
				lang: 'tag.contact.salutation',
				raw: false,
				group: 'Contact'
			});
		}

		const standardFieldTags: MailTagDefinition[] = [
			{ tag: 'Client.OrgNo', lang: 'tag.account.orgNo', raw: false, group: 'Client' },
			{ tag: 'Client.RegistrationDate', lang: 'tag.account.registrationDate', raw: false, group: 'Client' },
			{ tag: 'Client.SniCode', lang: 'tag.account.sniCodeCode', raw: false, group: 'Client' },
			{ tag: 'Client.SicCode', lang: 'tag.account.sicCodeCode', raw: false, group: 'Client' },
			{ tag: 'Client.NaicsCode', lang: 'tag.account.naicsCodeCode', raw: false, group: 'Client' },
			{ tag: 'Client.UKSicCode', lang: 'default.companyBranch.uksic', raw: false, group: 'Client' },
			{ tag: 'Client.NaceCode', lang: 'default.companyBranch.nace', raw: false, group: 'Client' },
			{ tag: 'Client.Status', lang: 'tag.account.statusCode', raw: false, group: 'Client' },
			{ tag: 'Client.CompanyForm', lang: 'tag.account.companyFormCode', raw: false, group: 'Client' },
			{ tag: 'Client.CreditRating', lang: 'tag.account.creditRatingCode', raw: false, group: 'Client' },
			{ tag: 'Client.Turnover', lang: 'tag.account.turnover', raw: false, group: 'Client' },
			{ tag: 'Client.Profit', lang: 'tag.account.profit', raw: false, group: 'Client' },
			{ tag: 'Client.NoEmployees', lang: 'tag.account.noEmployees', raw: false, group: 'Client' },
			{ tag: 'Client.Facebook', lang: 'tag.account.facebook', raw: false, group: 'Client' },
			{ tag: 'Client.Twitter', lang: 'tag.account.twitter', raw: false, group: 'Client' },
			{ tag: 'Client.LinkedIn', lang: 'tag.account.linkedIn', raw: false, group: 'Client' }
		];

		// Each active field is spliced in at the same fixed index, so they end up in reverse order
		// of this list. Kept verbatim from the Angular original.
		standardFieldTags.forEach(tag => {
			const fieldName = tag.tag.split('.')[1];
			if (clientFields[fieldName] && clientFields[fieldName].active) {
				tags.splice(26, 0, tag);
			}
		});
	}

	const popularTags = tags.reduce<MailTagDefinition[]>((result, tag) => {
		if (tag.isPopular) {
			result.push({ ...tag, group: 'Popular' });
		}
		return result;
	}, []);

	cached = { tags, popularTags };
	return cached;
};

/*
	Port of TagService.getMailTags. Note the contact custom field tags are built with group 'Client',
	not 'Contact' — they are appended to the Contact group by position, so the property is unused and
	the mismatch is harmless. Kept verbatim.
*/
export const getMailTags = (hideIdTags?: boolean): MailTagGroup[] => {
	const { tags, popularTags } = buildMailTags();

	const accountCustomTags: MailTag[] = AppService.getCustomFields('account').map((cf: CustomField) => ({
		value: '{{Client.custom_' + cf.id + '}}',
		name: cf.name,
		raw: false,
		group: 'Client'
	}));
	const contactCustomTags: MailTag[] = AppService.getCustomFields('contact').map((cf: CustomField) => ({
		value: '{{Contact.custom_' + cf.id + '}}',
		name: cf.name,
		raw: false,
		group: 'Client'
	}));

	const grouped = tags.concat(popularTags).reduce<{ [group: string]: MailTagDefinition[] }>((result, tag) => {
		(result[tag.group] = result[tag.group] || []).push(tag);
		return result;
	}, {});

	return Object.keys(grouped).map(group => {
		let subtags = grouped[group].reduce<MailTag[]>((result, tag) => {
			if (hideIdTags && tag.idTag) {
				return result;
			}
			result.push(getTag(tag.tag, t(tag.lang), tag.raw, tag.html));

			return result;
		}, []);
		if (group === 'Client') {
			subtags = [...subtags, ...accountCustomTags];
		}
		if (group === 'Contact') {
			subtags = [...subtags, ...contactCustomTags];
		}
		return {
			title: 'tag.group.' + group,
			group: group,
			tags: sortByName(subtags),
			sort: getGroupSort(group)
		};
	});
};

export const getSocialEventTags = (socialEvent: Record<string, unknown>): MailTagGroup[] => {
	const group = 'SocialEvent';
	const socialEventTags: LiveTagMap = getLiveTags(socialEvent as SocialEventLike);

	return [
		{
			title: 'default.socialEvents',
			group,
			tags: Object.keys(socialEventTags).reduce<MailTag[]>((tags, key) => {
				const tag = socialEventTags[key];
				if (!tag.hidden) {
					tags.push(
						tag?.html
							? tag
							: {
									name: tag.name,
									value: `{{${key}}}`
							  }
					);
				}
				return tags;
			}, []),
			sort: getGroupSort(group)
		}
	];
};

/*
	Port of TagsService.getTagsByEntity2. It falls back to the whole mail catalogue whenever the
	entity has no row in tagsByEntity — which is the path both React callers take, since neither
	passes a tagEntity. It also MUTATES the EntityTags catalogue entries (tag.value = ...), the same
	pre-existing quirk getEntityTags carries. Both kept verbatim.
*/
export const getTagsByEntity2 = (config: MailTagListConfig = {}): MailTagGroup[] => {
	const { tags, tagsByEntity, nameMapper, customFieldMapper } = getTagCatalogue();
	const t: MailTagGroup[] = [];
	const entities = config.tagEntity ? tagsByEntity[config.tagEntity] : undefined;
	if (entities === undefined || entities === null) {
		return getMailTags(config.hideIdTags);
	}
	const entityList = [config.tagEntity as string, ...entities];

	entityList.forEach(entity => {
		const customFields: MailTag[] = [];
		if (Object.prototype.hasOwnProperty.call(nameMapper, entity)) {
			const customFieldEntity = customFieldMapper[entity] ?? entity;
			const fields = AppService.getCustomFields(customFieldEntity as CustomFieldKey);
			fields.forEach((cf: CustomField) => {
				customFields.push({
					name: cf.name,
					tag: nameMapper[entity] + '.custom_' + cf.id,
					description: '',
					type: cf.datatype
				} as MailTag);
			});
		}

		const allTags = (tags[entity] as unknown as MailTag[]).concat(customFields).map(tag => {
			tag.value = '{{' + tag.tag + '}}';
			return tag;
		});

		if (allTags.length) {
			t.push({
				group: nameMapper[entity],
				tags: allTags,
				title: 'tag.group.' + nameMapper[entity]
			});
		}
	});

	return t;
};

const getFieldTranslationLanguages = async (): Promise<Language[]> => {
	const rb = new RequestBuilder();
	rb.addFilter({ field: 'type' }, rb.comparisonTypes.Equals, 'salutation');

	try {
		const languagesResponse = await Promise.all([
			AppService.getStaticValuesPromise('languages') as unknown as Promise<Language[]>,
			FieldTranslation.find(rb.build())
		]);

		const defaultLanguages: Language[] = languagesResponse[0] || [];
		const fieldTranslations: Language[] = languagesResponse[1] ? languagesResponse[1].data : [];
		const selectedLanguages = new Set(fieldTranslations.map(translation => translation.language));

		return defaultLanguages.map(lang => ({
			...lang,
			isSelected: selectedLanguages.has(lang.language)
		}));
	} catch (error) {
		logError(error, 'Failed to load field translation languages. Error:');
		return [];
	}
};

const insertSalutationMailTags = (mailTags: MailTagGroup[], languages: Language[]): MailTagGroup[] => {
	const contactGroupIndex = mailTags.findIndex(({ group }) => group === 'Contact');
	if (contactGroupIndex > -1) {
		const contactGroup = mailTags[contactGroupIndex];
		contactGroup.tags = contactGroup.tags.filter(tag => tag.value !== '{{Contact.SalutationValue}}');

		const salutationTag = contactGroup.tags.filter(tag => tag.value === '{{Contact.Salutation}}');
		contactGroup.tags = contactGroup.tags.filter(tag => tag.value !== '{{Contact.Salutation}}');
		if (salutationTag.length) {
			const salutationGroupTitle = 'default.salutation';
			const salutationSortValue = (contactGroup.sort as number) + 1;
			const salutationGroup: MailTagGroup = {
				title: salutationGroupTitle,
				class: 'th-inner--translation',
				group: 'Contact',
				tags: languages.reduce<MailTag[]>((tags, language) => {
					if (language.isSelected) {
						tags.push({
							value: '{{Translate_' + language.language + '_Contact.SalutationValue}}',
							name: language.name,
							raw: false,
							group: 'Contact',
							icon: language.country ? 'flag-icon flag-icon-' + language.country : ''
						});
					}
					return tags;
				}, []),
				sort: salutationSortValue
			};

			const withSalutationGroup = [
				...mailTags.slice(0, contactGroupIndex + 1),
				salutationGroup,
				...mailTags.slice(contactGroupIndex + 1)
			];
			mailTags = withSalutationGroup.map(group => {
				if (group.title !== salutationGroupTitle && (group.sort as number) >= salutationSortValue) {
					return {
						...group,
						sort: (group.sort as number) + 1
					};
				}

				return group;
			});
		}
	}
	return mailTags;
};

export const generateFormSubmitTags = (): MailTagGroup => {
	const formSubmitTags: MailTag[] = [];

	formSubmitTags.push({
		name: t('form.form2') + ' ' + t('default.name'),
		value: '{{FormSubmit.Form.Name}}'
	});

	// FormSubmit.Extra.* tags - Form-specific fields
	// Extra fields use the pattern: Extra.<timestamp> where timestamp is from Date.now()
	// We use a real timestamp example, but note that actual values differ per form
	formSubmitTags.push({
		name: t('form.extraFieldType'),
		value: '{{FormSubmit.Extra.<id>}}',
		subtitle: t('form.copyIdFromForm')
	});

	const clientStandardFields = [
		{ field: 'name', name: t('mailTags.client_name') },
		{ field: 'phone', name: t('mailTags.client_phone') },
		{ field: 'webpage', name: t('tag.account.www') },
		{ field: 'address', name: t('tag.account.address') },
		{ field: 'zipcode', name: t('tag.account.zipcode') },
		{ field: 'city', name: t('tag.account.city') },
		{ field: 'country', name: t('tag.account.country') },
		{ field: 'orgNo', name: t('tag.account.orgNo') },
		{ field: 'noEmployees', name: t('tag.account.noEmployees') },
		{ field: 'facebook', name: t('tag.account.facebook') },
		{ field: 'twitter', name: t('tag.account.twitter') },
		{ field: 'linkedin', name: t('tag.account.linkedIn') }
	];

	clientStandardFields.forEach(field => {
		formSubmitTags.push({
			name: field.name,
			value: '{{FormSubmit.Client.' + field.field + '}}'
		});
	});

	AppService.getCustomFields('account').forEach((cf: CustomField) => {
		formSubmitTags.push({
			name: cf.name,
			value: '{{FormSubmit.Client.custom_' + cf.id + '}}'
		});
	});

	const contactStandardFields = [
		{ field: 'firstname', name: t('mailTags.fname') },
		{ field: 'lastname', name: t('mailTags.lname') },
		{ field: 'email', name: t('mailTags.email') },
		{ field: 'title', name: t('tag.contact.title') },
		{ field: 'phone', name: t('tag.contact.phone') },
		{ field: 'cellPhone', name: t('tag.contact.cellphone') }
	];

	contactStandardFields.forEach(field => {
		formSubmitTags.push({
			name: field.name,
			value: '{{FormSubmit.Contact.' + field.field + '}}'
		});
	});

	AppService.getCustomFields('contact').forEach((cf: CustomField) => {
		formSubmitTags.push({
			name: cf.name,
			value: '{{FormSubmit.Contact.custom_' + cf.id + '}}'
		});
	});

	return {
		title: 'tag.formsubmit',
		group: 'FormSubmit',
		tags: formSubmitTags,
		sort: 7
	};
};

export const getMailTagList = async (tags: MailTagListConfig = {}): Promise<MailTagGroup[]> => {
	let validMailTags: MailTagGroup[] = getTagsByEntity2(tags);
	if (tags.socialEvent) {
		const socialEventTags = getSocialEventTags(tags.socialEvent);
		validMailTags = [...validMailTags, ...socialEventTags];
	}

	if (!tags.excludeFormSubmit) {
		const formSubmitTags = generateFormSubmitTags();
		validMailTags = [...validMailTags, formSubmitTags];
	}

	if (hasSoftDeployAccess(Feature.NEW_FIELDS)) {
		const languages = await getFieldTranslationLanguages();
		if (!languages.length) {
			return validMailTags;
		}
		validMailTags = insertSalutationMailTags([...validMailTags], languages);
	}

	return validMailTags;
};

export const flattenMailTagList = async (tags: MailTagListConfig = {}): Promise<string[]> => {
	const mailTags = await getMailTagList(tags);
	const groupMailTags = mailTags.reduce<string[]>((acc, { tags }) => {
		const groupTags = tags.map(({ value }) => value);
		return [...acc, ...groupTags];
	}, []);

	const pattern = /{{[^}]+}}/g;
	const uniqueMailTags = new Set<string>();

	groupMailTags.forEach(tag => {
		const matches = tag.match(pattern);
		if (matches) {
			matches.forEach(match => uniqueMailTags.add(match));
		}
	});

	return Array.from(uniqueMailTags);
};

/*
	Only for the Angular remainder's getTags/getTagsBee, which need the raw catalogue rather than the
	grouped mail tags. It exists so there is still exactly ONE catalogue, and goes away with the
	Angular tree. Do not reach for it from React.
*/
export const getMailTagCatalogue = () => buildMailTags();
